import closeIcon from '@disy/cadenza-icons/close.svg';
import h from 'hyperscript';
import $ from 'jquery';
import { unByKey } from 'ol/Observable';
import Sortable from 'sortablejs';

import { Sidebar } from 'ui/sidebar/sidebar';
import 'ui/compact-header/compact-header.css';

import { LegendLayer } from 'cadenza/map/legend/layer/legend-layer';
import { context } from 'cadenza/map/context/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { on, unByKey as un } from 'cadenza/utils/event-util';
import { icon } from 'cadenza/utils/icon/icon';
import { getLayerTasks } from 'cadenza/map/layer-tasks';
import {
  BlockIdentifier,
  getBlockSettingsSingleton
} from 'cadenza/map/block-settings';

import './legend.css';
import i18n from './messages.properties';

const COMPONENT_NAME = 'd-map-legend';
const LAYER_SELECTION_CLASS = 'with-layer-selection';

export class Legend extends Sidebar {

  #map;

  constructor (map, options = {}) {
    super();
    this.#map = map;
    this.options = {
      layerSelection: true,
      ...options
    };

    this._setLayerSelectionAllowed(this.options.layerSelection);

    const blockSettings = getBlockSettingsSingleton();
    this.addEventListener('close', () => {
      blockSettings.setVisibility(BlockIdentifier.MAP_LEGEND, 'minimized');
    });
  }

  connectedCallback () {
    const blockSettings = getBlockSettingsSingleton();
    if (blockSettings.isBlockSettingPresent(BlockIdentifier.MAP_LEGEND)) {
      const minimized = blockSettings.isBlockMinimized(BlockIdentifier.MAP_LEGEND);
      this.open = !minimized;
    } else if (this.options.isMinimized !== undefined) {
      this.open = !this.options.isMinimized;
    }
    super.connectedCallback();
    this.id = 'legend';
    this.classList.add(COMPONENT_NAME);
    this.dataset.testid = 'map-legend';

    this.append(
      h('header.d-compact-header', [
        h('label.d-compact-header--title', i18n('title')),
        h('button.d-sidebar-close.button.button-icon.button-borderless', {
          type: 'button',
          title: i18n('common:close')
        }, icon(closeIcon))
      ]),
      this._layerList = h('.d-sidebar-content', { attrs: { role: 'list' } })
    );

    const map = this.#map;
    const layers = map.getLayers();
    this._onChange();
    this._eventKeys = [
      layers.on('change', () => this._onChange()),
      layers.on('remove', () => this._onChange()),
      map.on(`change:${MapProperty.EDITING_LAYER}`, () => this._onEditingLayerChange())
    ];

    if (this.options.layerOrdering !== false) {
      // https://github.com/SortableJS/Sortable/tree/master?tab=readme-ov-file#options
      const sortableOptions = {
        onUpdate: (event) => this._moveLegendLayer(event.oldIndex, event.newIndex),
        delay: 100, // Delays dragging and allows scrolling in some android chrome versions
        delayOnTouchOnly: true, // The delay reduces the user experience for non-touch devices
        fallbackTolerance: 8 // Prioritize clicking over dragging in some android chrome versions
      };
      Sortable.create(this._layerList, sortableOptions);
    }
    if (this.options.layerRemoval && $.support.touch) {
      initializeRemoveOnSwipeRight(this._layerList, this.#map);
    }
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  _onEditingLayerChange () {
    this._setLayerSelectionAllowed(!this.#map.get(MapProperty.EDITING_LAYER));
    this._onChange();
  }

  _setLayerSelectionAllowed (allowSelection) {
    if (allowSelection && !this._clickEventKey) {
      this.classList.add(LAYER_SELECTION_CLASS);
      this._clickEventKey = on(this, 'click', (e) => this.onClickLayer(e), { delegate: '.d-map-layer' });
    } else if (!allowSelection && this._clickEventKey) {
      un(this._clickEventKey);
      this._clickEventKey = undefined;
      this.classList.remove(LAYER_SELECTION_CLASS);
    }
  }

  getLegendLayerById (layerId) {
    for (const legendLayer of this.querySelectorAll('d-map-layer')) {
      if (legendLayer.layer.get(LayerProperty.ID) === layerId) {
        return legendLayer;
      }
    }
  }

  /**
   * Returns the visible legend layers.
   *
   * @returns {LegendLayer[]} the layers which are visible
   */
  visibleLegendLayers () {
    return Array.from(this.querySelectorAll('d-map-layer'))
      .filter(legendLayer => legendLayer.layer.get(LayerProperty.VISIBLE_IN_LAYERLIST));
  }

  legendTasksRunning () {
    return Array.from(this.querySelectorAll('d-map-layer'))
      .some(legendLayer => getLayerTasks(legendLayer.layer).hasOngoingTasks());
  }

  onClickLayer (event) {
    const clickedLayer = event.delegateTarget.layer;
    const currentlySelectedLayer = this.#map.get(MapProperty.SELECTED_LAYER);
    if (clickedLayer !== currentlySelectedLayer) {
      this.#map.set(MapProperty.SELECTED_LAYER, clickedLayer);
    } else {
      this.#map.unset(MapProperty.SELECTED_LAYER);
    }
  }

  _onChange () {
    const map = this.#map;
    const visible = (layer) => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST);
    const visibleLayers = context(map).layers.filter(visible);

    const sidebarContent = this.querySelector('.d-sidebar-content');
    sidebarContent.innerHTML = '';
    sidebarContent.append(...visibleLayers.reverse().map(layer => new LegendLayer(layer, map, this.options)));
  }

  _moveLegendLayer (fromIndex, toIndex) {
    const mapLayers = this.#map.getLayers().getArray();
    const fromIndexReversed = mapLayers.length - 1 - getAbsoluteLayerIndex(mapLayers, fromIndex);
    const toIndexReversed = mapLayers.length - 1 - getAbsoluteLayerIndex(mapLayers, toIndex);

    mapLayers.splice(toIndexReversed, 0, mapLayers.splice(fromIndexReversed, 1)[0]);
    this.#map.getLayers().changed(); // Update the legend gui
    this.#map.getLayers().dispatchEvent('propertychange'); // Push to the server
    this.#map.render();
  }

}

function getAbsoluteLayerIndex (layers, visibleReversedPosition) {
  const noOfInvisibleLayers = countInvisibleLayers(layers, visibleReversedPosition);
  return visibleReversedPosition + noOfInvisibleLayers;
}

function countInvisibleLayers (layers, visibleReversedPosition) {
  let counter = 0;
  for (let i = layers.length - 1; i > visibleReversedPosition; i--) {
    if (!layers[i].get(LayerProperty.VISIBLE_IN_LAYERLIST)) {
      counter++;
    }
  }
  return counter;
}

function initializeRemoveOnSwipeRight (layerList, map) {
  const layerItemClass = '.d-map-layer';
  let touchstartPosition, $swipedElement;

  $(layerList).on('touchstart', layerItemClass, function (event) {
    touchstartPosition = touchPosition(event);
  }).on('touchmove', layerItemClass, function (event) {
    if (!$(layerList).sortable('instance').helper) { // do not swipe while sorting
      const p = touchPosition(event);

      if (!$swipedElement && isSwipeRight(touchstartPosition, p)) {
        $swipedElement = $(event.currentTarget).addClass('dragging');
      }

      if ($swipedElement) {
        const dx = Math.max(0, p.x - touchstartPosition.x);
        $swipedElement.css({
          transform: 'translateX(' + dx + 'px)',
          opacity: 1 - (2 * dx / $swipedElement.width()) // Opacity => 0 when swiped half the width.
        });
        return false; // Stop event bubbling to prevent scrolling.
      }
    }
  }).on('touchend', layerItemClass, function (event) {
    if ($swipedElement) {
      if ($swipedElement.css('opacity') === '0') {
        map.removeLayer(this.layer);
      } else {
        $swipedElement.removeClass('dragging').css({ transform: '', opacity: '' });
      }
    }

    touchstartPosition = null;
    $swipedElement = null;
  });
}

function isSwipeRight (a, b) {
  if (!a || !b) {
    return false;
  }
  const angle = angleBetweenPoints(a, b);
  return (angle >= -15 && angle <= 15); // True if vector points to right having ±15 degrees tolerance.
}

function angleBetweenPoints (a, b) {
  return Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
}

function touchPosition (event) {
  const touch = event.originalEvent.touches[0];
  return { x: touch.pageX, y: touch.pageY };
}

customElements.define(COMPONENT_NAME, Legend);
